'use client';

import React, { useState } from 'react';
import { BsPencil } from 'react-icons/bs';
import { IoArrowForwardOutline, IoBriefcaseOutline, IoSearchOutline } from 'react-icons/io5';

import { Box, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { SpotlightActionData, SpotlightActionGroupData } from '@mantine/spotlight';
import { Spotlight as MantineSpotlight } from '@mantine/spotlight';
import useSWR from 'swr';

import { all as allComponentTypes } from 'models';
import { onlyLooseMatches } from 'helpers/only-loose-matches';
import { RouterHelpers } from 'helpers/RouterHelpers';

import { ComponentIcon } from 'components/component-icons/ComponentIcon';
import { useHeaderState } from 'components/header/hooks/use-header-state';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { CompanyProfileService } from 'services/CompanyProfileService';
import { ComponentService } from 'services/ComponentService';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { ProjectService } from 'services/ProjectService';
import { RouterService } from 'services/RouterService';
import { SpotlightService } from 'services/SpotlightService';

import cx from './Spotlight.module.scss';

const REFERENCE_DESIGN_INTENT_PATTERNS = [/(ref(erence)?\s*)?designs?/i];
const isReferenceDesignIntent = (q: string) => REFERENCE_DESIGN_INTENT_PATTERNS.some((p) => p.test(q));

const Spotlight = () => {
    const [query, setQuery] = useState('');
    const { showResourceProNav } = useHeaderState();
    const workspaceConfig = useWorkspaceConfig();

    const searchPlaceholder =
        workspaceConfig.workspace && workspaceConfig.name
            ? `Search ${workspaceConfig.name}`
            : showResourceProNav
              ? 'Search RE+Source PRO'
              : 'Search DEP';

    const [debouncedQuery] = useDebouncedValue(query, 300);

    const key = debouncedQuery ? SpotlightService.swr.search(debouncedQuery) : null;
    const { data, isLoading } = useSWR(key, async () => {
        if (debouncedQuery.length < 2) {
            return;
        }

        return SpotlightService.search(debouncedQuery);
    });

    const { companies, components, projects, referenceDesigns, industrySubcategories } = data || {
        companies: [],
        components: [],
        projects: [],
        referenceDesigns: [],
        industrySubcategories: [],
    };

    const actions: (SpotlightActionGroupData | SpotlightActionData)[] = [
        {
            group: 'Reference Designs',
            actions: [
                ...referenceDesigns.map((project) => ({
                    id: project.id,
                    label: project.name,
                    keywords: [query],
                    leftSection: <BsPencil size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-project', {
                            query,
                            project: project.id,
                        });

                        ProjectService.navigate.editor(project.id).then();
                    },
                    hidden: false,
                })),
                {
                    id: 'reference-designs-search',
                    label: 'Find Reference Designs',
                    keywords: [query],
                    itemType: 'search',
                    leftSection: <IoSearchOutline size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-reference-designs-search', {
                            query,
                        });

                        RouterService.push(RouterHelpers.urls.designs.overview());
                    },
                    hidden: !query || (!referenceDesigns.length && !isReferenceDesignIntent(query)),
                },
            ],
        },
        {
            group: onlyLooseMatches(companies) ? 'PROs · closest matches' : 'PROs',
            actions: [
                ...companies.map((company) => ({
                    id: company.id,
                    label: company.name,
                    keywords: [query],
                    leftSection: <IoBriefcaseOutline size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-company', {
                            query,
                            company: company.id,
                        });

                        CompanyProfileService.navigate.view(company.slug).then();
                    },
                })),
                ...industrySubcategories.map((industrySubcategory) => ({
                    id: industrySubcategory.id,
                    label: `All ${industrySubcategory.name}`,
                    keywords: [query],
                    leftSection: <IoArrowForwardOutline size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-industry-subcategory', {
                            query,
                            industrySubcategory: industrySubcategory.id,
                        });

                        RouterService.push(RouterHelpers.urls.profiles.categories(industrySubcategory.slug!));
                    },
                })),
                {
                    id: 'company-search',
                    label: 'Find Services',
                    keywords: [query],
                    itemType: 'search',
                    leftSection: <IoSearchOutline size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-company-search', {
                            query,
                        });

                        RouterService.push(RouterHelpers.urls.profiles.search());
                    },
                    hidden: !query || !(companies.length || industrySubcategories.length),
                },
            ],
        },
        {
            group: onlyLooseMatches(components) ? 'Products · closest matches' : 'Products',
            actions: [
                ...components.map((component) => ({
                    id: component.id,
                    label: `${component.name} ${component.productIdentifier && `(${component.productIdentifier})`}`,
                    keywords: [query],
                    leftSection: (
                        <Box w={14} h={14} style={{ overflow: 'hidden' }}>
                            <ComponentIcon type={component.type} />
                        </Box>
                    ),
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-component', {
                            query,
                            component: component.id,
                        });

                        ComponentService.navigate.view(component.id).then();
                    },
                })),
                ...Object.values(allComponentTypes).map((component) => ({
                    id: component.type,
                    label: `All ${component.plural}`,
                    leftSection: <IoArrowForwardOutline size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-product-type', {
                            query,
                            component: component.type,
                        });

                        RouterService.push(
                            RouterHelpers.urls.products.search({ productsQuery: { type: component.type } }),
                        );
                    },
                    hidden: !query,
                })),
                {
                    id: 'product-search',
                    label: `Find More Products for "${query}"`,
                    keywords: [query],
                    itemType: 'search',
                    leftSection: <IoSearchOutline size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-product-search', {
                            query,
                        });

                        RouterService.push(RouterHelpers.urls.products.search({ productsQuery: { search: query } }));
                    },
                    hidden: !query || !components.length,
                },
            ],
        },

        {
            group: 'Projects',
            actions: [
                ...projects.map((project) => ({
                    id: project.id,
                    label: project.name,
                    keywords: [query],
                    leftSection: <BsPencil size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-project', {
                            query,
                            project: project.id,
                        });

                        ProjectService.navigate.editor(project.id).then();
                    },
                })),
                {
                    id: 'project-search',
                    label: 'Go to My Projects',
                    keywords: [query],
                    itemType: 'search',
                    leftSection: <IoArrowForwardOutline size={14} />,
                    onClick: () => {
                        InternalTrackingService.track('spotlight.navigate-project-search', {
                            query,
                        });

                        RouterService.push(RouterHelpers.urls.projects.overview());
                    },
                    hidden: !query || !projects.length,
                },
            ],
        },
    ];

    const quicklinks: SpotlightActionGroupData = {
        group: 'Quick Links',
        actions: [
            {
                id: 'company-search',
                label: 'Find Services',
                keywords: ['navigate', 'company', 'companies', 'professional'],
                leftSection: <IoSearchOutline size={14} />,
                onClick: () => {
                    InternalTrackingService.track('spotlight.navigate-company', {
                        query,
                    });

                    RouterService.push('/profiles');
                },
            },
            {
                id: 'component-search',
                label: 'Find Products',
                keywords: ['navigate', 'component', 'products'],
                leftSection: <IoSearchOutline size={14} />,
                onClick: () => {
                    InternalTrackingService.track('spotlight.navigate-products', {
                        query,
                    });

                    RouterService.push(RouterHelpers.urls.products.search());
                },
            },
            {
                id: 'designs',
                label: 'Find Reference Designs',
                keywords: ['navigate', 'designs'],
                leftSection: <IoSearchOutline size={14} />,
                onClick: () => {
                    InternalTrackingService.track('spotlight.navigate-designs', {
                        query,
                    });

                    RouterService.push(RouterHelpers.urls.designs.search());
                },
            },
        ],
    };

    if (!query) {
        actions.push(quicklinks);
    }

    return (
        <MantineSpotlight
            shortcut={['mod + K', 'mod + P', '/']}
            actions={actions.map((group) => ({
                ...group,
                ...('actions' in group ? { actions: group.actions.filter((action) => !action.hidden) } : {}),
            }))}
            query={query}
            onQueryChange={setQuery}
            searchProps={{
                leftSection: <IoSearchOutline size={20} />,
                placeholder: searchPlaceholder,
                rightSection: isLoading && <Loader size="xs" />,
            }}
            classNames={{
                actionsGroup: cx.actionsGroup,
                action: cx.action,
                actionSection: cx.actionSection,
            }}
            overlayProps={{
                blur: 0,
                opacity: 0.5,
            }}
            nothingFound="No results found"
            highlightQuery
            size="lg"
        />
    );
};

export { Spotlight };
